import * as React from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'

export interface AnchoredMenuProps {
  isOpen: boolean
  onClose: () => void
  /** dialog label, and the heading of the mobile sheet */
  title: string
  children?: React.ReactNode
}

/**
 * Renders as a dropdown anchored to its nearest positioned ancestor on desktop and as a
 * bottom sheet below md. The trigger must share that `relative` parent.
 */
export const AnchoredMenu: React.FC<AnchoredMenuProps> = ({ isOpen, onClose, title, children }) => {
  React.useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)

    // scroll lock only for the mobile sheet; the desktop dropdown scrolls with its anchor
    const isMobileSheet = window.matchMedia('(max-width: 767px)').matches
    if (isMobileSheet) document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleEscape)
      if (isMobileSheet) document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  // stay mounted while the close animation plays
  const [isVisible, setIsVisible] = React.useState(false)
  const [isAnimating, setIsAnimating] = React.useState(false)
  React.useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
    } else {
      setIsAnimating(false)
      const timer = setTimeout(() => setIsVisible(false), 200)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // start the enter transition only after the panel is mounted and has painted
  // its closed state — flipping the class in the mount effect skips the animation
  React.useEffect(() => {
    if (!isVisible || !isOpen) return
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setIsAnimating(true))
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [isVisible, isOpen])

  if (!isVisible) return null

  return (
    <>
      {/* click-catcher: dimmed backdrop on mobile, invisible on desktop; above the fixed navbar (z-50) */}
      <div
        className={`fixed inset-0 z-[60] bg-gray-500/75 transition-opacity duration-200 md:bg-transparent ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-label={title}
        className={`fixed inset-x-0 bottom-0 z-[70] rounded-t-2xl bg-white p-4 pb-8 shadow-xl transition-all duration-200 ease-out md:absolute md:inset-x-auto md:bottom-auto md:right-0 md:top-full md:mt-2 md:w-80 md:origin-top-right md:rounded-lg md:border md:border-gray-200 md:pb-4 md:shadow-lg ${
          isAnimating
            ? 'translate-y-0 md:scale-100 md:opacity-100'
            : 'translate-y-full md:translate-y-0 md:scale-95 md:opacity-0'
        }`}
      >
        {/* mobile sheet header */}
        <div className="md:hidden">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-medium text-gray-900">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <span className="sr-only">Zamknij</span>
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-4">{children}</div>
      </div>
    </>
  )
}
